import React from "react";
import {Badge} from "antd";
import {ApartmentOutlined, CustomerServiceOutlined, MailOutlined, SafetyCertificateFilled, SolutionOutlined} from "@ant-design/icons";
import {
    AbzeichenSvg,
    AttendeesSvg,
    CoursesSvg,
    DashboardSvg,
    InvoicesSvg,
    LeftMenuBookingIcon,
    LocationsSvg,
    NotificationsSvg,
    SettingsSvg,
    StaffLoginSvg,
    StaffSvg,
    WithdrawSvg,
    TransactionsSvg,
    VorlagenSvg,
    WeekPlannerSvg,
} from "../../assets/SVG-Icons";
import {MenuNavigationItem, URL_TO_KEY_MAP} from "../../views/LayoutStandard/types";
import i18n from "../../services/i18n";
import {
    URL_AREA_MANAGER,
    URL_AREA_MANAGER_ASSISTANTS,
    URL_AREA_MANAGER_COURSES,
    URL_AREA_MANAGER_INSTRUCTORS,
    URL_AREA_MANAGER_OVERVIEW,
    URL_AREA_MANAGER_VENUES,
    URL_ATTENDEES,
    URL_BADGES,
    URL_BADGES_ASSIGN,
    URL_BOOKERS,
    URL_BOOKINGS,
    URL_BOOKINGS_COURSE_LIST,
    URL_BOOKINGS_LIST,
    URL_COURSES,
    URL_COURSES_CATEGORIES,
    URL_COURSES_EDIT,
    URL_COURSES_PREREQUISITE,
    URL_COURSES_TYPES,
    URL_COURSES_DISCOUNT_CODES,
    URL_CUSTOMERS,
    URL_CUSTOMERS_DUPLICATES,
    URL_DASHBOARD,
    URL_INVOICE,
    URL_INVOICE_LIST_BY_BOOKING_CANCELED,
    URL_INVOICE_LIST_BY_BOOKING_SETTLED,
    URL_INVOICE_LIST_BY_BOOKING_UNSETTLED,
    URL_INVOICE_LIST_BY_COURSE,
    URL_LESSONS_TODAY,
    URL_LESSONS_TODAY_CALENDAR,
    URL_LESSONS_TODAY_CALENDAR_WEEKVIEW,
    URL_LESSONS_TODAY_CALENDAR_DAYVIEW,
    URL_LESSONS_TODAY_EXPORT_INSTRUCTOR_LESSONS,
    URL_LOCATIONS,
    URL_LOCATIONS_CONTACTS,
    URL_LOCATIONS_OVERVIEW,
    URL_MY_BOOKINGS,
    URL_MY_BOOKINGS_COURSE_LIST,
    URL_MY_BOOKINGS_LIST,
    URL_NOTIFICATIONS,
    URL_NOTIFICATIONS_FIXER,
    URL_NOTIFICATIONS_SENT_MAILS,
    URL_OPEN,
    URL_PAID,
    URL_PAYMENTS,
    URL_SETTINGS,
    URL_HELP,
    URL_SETTINGS_ADMIN,
    URL_SETTINGS_ENDUSERAPP,
    URL_SETTINGS_GENERAL,
    URL_SETTINGS_IMPORT_BOOKERS,
    URL_SETTINGS_DATA_EXPORT,
    URL_SETTINGS_INVOICE_EXPORT,
    URL_SETTINGS_MAINTENANCE,
    URL_SETTINGS_MONITORING,
    URL_STAFF,
    URL_STAFF_LOGIN,
    URL_STAFF_LOGIN_FILES,
    URL_STAFF_LOGIN_START,
    URL_STAFF_OVERVIEW,
    URL_TEMPLATES,
    URL_TEMPLATES_EMAIL,
    URL_TEMPLATES_INVOICES,
    URL_STAFF_ADMINS,
    URL_STAFF_BILLING_STATUS,
    URL_COURSES_BULK_SEND,
    URL_CERTIFICATES,
    URL_CERTIFICATES_ISSUED,
    URL_CERTIFICATES_TEMPLATES,
    URL_IMPORT_HISTORY,
    URL_PAYMENTS_REFUNDS,
    URL_MOLLIE_PAYMENTS,
    URL_CHARGEBACKS,
    URL_PAYMENTS_BANK_IMPORT,
    URL_WITHDRAWALS,
    URL_WITHDRAWALS_OVERVIEW,
    URL_INTEREST_LISTS,
    URL_NEWSLETTER,
    URL_NEWSLETTER_SETTINGS,
    URL_NEWSLETTER_LISTS,
    URL_NEWSLETTER_OVERVIEW,
    URL_INTEREST_LISTS_MANAGE,
    URL_INTEREST_REGISTRATIONS,
} from "../../models/url";
import Roles from "../../../shared/models/roles";
import {StaffVisibilityArea} from "../../../shared/models/staffVisibility";
import {routePermissions} from "./AuthenticateSetup";
import css from "./MainMenuRoutes.less";
import classNames from "classnames";

// const renderIcon = (icon: React.ReactNode) => {
//     const style = {
//         // fontSize: "1.3em",
//         color: "inherit",
//     };

//     return <div className={css.iconContainer}>{icon}</div>;
// };

// Menüpunkt mit Beta-Chip; top: 1.5px gleicht die optische Mitte zur x-Höhe des Labels aus.
const MenuBetaLabel = ({children}: {children: React.ReactNode}) => (
    <span style={{display: "inline-flex", alignItems: "center", gap: 6}}>
        {children}
        <span style={{position: "relative", top: 1.5, fontSize: 10, lineHeight: 1, padding: "2px 5px", border: "1px solid currentColor", borderRadius: 8, opacity: 0.7, fontWeight: 500, letterSpacing: 0.3}}>{"BETA"}</span>
    </span>
);

const IconContainer = ({icon, style, extraClass}: {icon: React.ReactNode; style?: React.CSSProperties; extraClass?: string}) => {
    return (
        <span className={classNames(css.iconContainer, extraClass)} style={style}>
            {icon}
        </span>
    );
};

export const getMainMenuItems = ({
    highestRole,
    rolesWithAccess,
    featureWeekPlaner,
    featurePrerequisiteCourses,
    featureDiscountCodes,
    featureExportInstructorLessons,
    featureStaffLogin,
    featureBillingStatus,
    featureAttendanceListBulk,
    featureExtendedBadges,
    featureAreaManager,
    featureDataExport,
    featureWebModulAdminAccess,
    featureEsr,
    featureMollie,
    featureBankImport,
    featureNewsletter,
    featureInterestList,
    featureAdminManagement,
    withdrawalsOpenCount,
    withdrawalButtonEnabled,
    isInstructor,
    rolesOfCurrent,
    hiddenAreas = [],
}: {
    highestRole: Roles;
    rolesWithAccess?: Array<Roles>;
    featureWeekPlaner?: boolean;
    featurePrerequisiteCourses?: boolean;
    featureDiscountCodes?: boolean;
    featureExportInstructorLessons?: boolean;
    featureStaffLogin?: boolean;
    featureBillingStatus?: boolean;
    featureAttendanceListBulk?: boolean;
    featureExtendedBadges?: boolean;
    featureAreaManager?: boolean;
    featureDataExport?: boolean;
    featureWebModulAdminAccess?: boolean;
    featureEsr?: boolean;
    // Phase 14 Plan 14-03 (D-18) — Feature-Gate für Refund-Menu-Item
    featureMollie?: boolean;
    // Bankabgleich (Beta) — gated „Bankabgleich“ unter Zahlungen, zusätzlich Rolle ab SuperAdmin.
    featureBankImport?: boolean;
    // Newsletter (Beta, Weg A0): gated den Bereich „Newsletter“.
    featureNewsletter?: boolean;
    // Interessenten-Listen-Feature (sc-3167) — gated das gleichnamige Menü.
    featureInterestList?: boolean;
    // Self-Service-Admin-Verwaltung — gated den „Admins"-Punkt der Mitarbeiter-Ansicht.
    featureAdminManagement?: boolean;
    // Anzahl offener (status "new") Widerrufe für das Menü-Badge.
    withdrawalsOpenCount?: number;
    // Widerrufe-Menüeintrag zeigen, wenn der Kundenportal-Button aktiv ist ODER es
    // offene Widerrufe gibt (Button-Setting ist Superuser/Web-Modul-gated).
    withdrawalButtonEnabled?: boolean;
    isInstructor?: boolean;
    rolesOfCurrent?: Array<Roles>;
    // #708/#709: Bereiche mit Sichtbarkeitsregel „Kein Zugriff“ → Menüpunkt ausblenden.
    hiddenAreas?: ReadonlyArray<StaffVisibilityArea>;
}): {[key: string]: MenuNavigationItem} => {
    const hasInstructorRole = rolesOfCurrent?.includes(Roles.Instructor) ?? false;
    const hidden = (area: StaffVisibilityArea) => hiddenAreas.includes(area);
    const mainMenuItems = {
        ...(routePermissions[URL_DASHBOARD].includes(highestRole)
            ? {
                  dashboard: {
                      label: i18n.views.LayoutStandard.dashboard(),
                      key: URL_TO_KEY_MAP.URL_DASHBOARD as keyof typeof URL_TO_KEY_MAP,
                      icon: <IconContainer icon={<DashboardSvg />} />,
                      link: URL_DASHBOARD,
                      children: [
                          {
                              key: "1 dashboard",
                              label: i18n.containers.dashboard.DashboardOverview.title(),
                              link: URL_DASHBOARD,
                              children: [],
                          },
                      ],
                  },
              }
            : {}),
        ...(!hidden("customers") && routePermissions[URL_CUSTOMERS].includes(highestRole)
            ? {
                  customers: {
                      label: i18n.views.LayoutStandard.customers(),
                      key: URL_TO_KEY_MAP.URL_CUSTOMERS as keyof typeof URL_TO_KEY_MAP,
                      //   icon: renderIcon(<AttendeesSvg />),
                      //   icon: <AttendeesSvg />,
                      icon: <IconContainer icon={<AttendeesSvg />} />,
                      link: URL_CUSTOMERS,
                      children: [
                          {
                              key: "1bookers",
                              label: i18n.containers.customers.Customers.bookers(),
                              link: URL_BOOKERS,
                              children: [],
                          },
                          {
                              key: "2 attendees",
                              label: i18n.containers.customers.Customers.attendees(),
                              link: URL_ATTENDEES,
                              children: [],
                          },
                          ...(routePermissions[URL_CUSTOMERS_DUPLICATES].includes(highestRole)
                              ? [
                                    {
                                        key: "3duplicates",
                                        label: i18n.containers.customers.Customers.duplicates(),
                                        link: URL_CUSTOMERS_DUPLICATES,
                                        children: [],
                                    },
                                ]
                              : []),
                      ],
                  },
              }
            : {}),
        ...(routePermissions[URL_COURSES].includes(highestRole)
            ? {
                  courses: {
                      label: i18n.views.LayoutStandard.courses(),
                      key: URL_TO_KEY_MAP.URL_COURSES as keyof typeof URL_TO_KEY_MAP,
                      icon: <IconContainer icon={<CoursesSvg />} />,
                      link: URL_COURSES,
                      children: [
                          {
                              key: "1 courses",
                              label: i18n.containers.courses.Courses.courses(),
                              link: URL_COURSES_EDIT,
                              children: [
                                  //   {
                                  //       key: "4 prerequisite-courses",
                                  //       label: i18n.containers.courses.Courses.prerequisiteCourses(),
                                  //       link: URL_COURSES_PREREQUISITE,
                                  //       children: [],
                                  //   },
                              ],
                          },
                          ...(!hidden("courseTypes") && routePermissions[URL_COURSES_TYPES].includes(highestRole)
                              ? [
                                    {
                                        key: "2 course-types",
                                        label: i18n.containers.courses.Courses.courseTypes(),
                                        link: URL_COURSES_TYPES,
                                        children: [],
                                    },
                                ]
                              : []),
                          ...(!hidden("courseTypes") && routePermissions[URL_COURSES_CATEGORIES].includes(highestRole)
                              ? [
                                    {
                                        key: "3 course-categories",
                                        label: i18n.containers.courses.Courses.courseCategories(),
                                        link: URL_COURSES_CATEGORIES,
                                        children: [],
                                    },
                                ]
                              : []),
                          ...(routePermissions[URL_COURSES_PREREQUISITE].includes(highestRole) && featurePrerequisiteCourses
                              ? [
                                    {
                                        key: "4 prerequisite-courses",
                                        label: i18n.containers.courses.Courses.prerequisiteCourses(),
                                        link: URL_COURSES_PREREQUISITE,
                                        children: [],
                                    },
                                ]
                              : []),
                          ...(featureAttendanceListBulk && routePermissions[URL_COURSES_BULK_SEND].includes(highestRole) && featureAttendanceListBulk
                              ? [
                                    {
                                        key: "5 attendance-list-bulk",
                                        label: i18n.containers.courses.Courses.attendanceListBulk(),
                                        link: URL_COURSES_BULK_SEND,
                                        children: [],
                                    },
                                ]
                              : []),
                          // Rabattcodes (Beta): Tenant-Flag UND Rolle ab Admin.
                          ...(featureDiscountCodes && routePermissions[URL_COURSES_DISCOUNT_CODES].includes(highestRole)
                              ? [
                                    {
                                        key: "6 discount-codes",
                                        label: <MenuBetaLabel>{i18n.containers.courses.Courses.discountCodes()}</MenuBetaLabel>,
                                        link: URL_COURSES_DISCOUNT_CODES,
                                        children: [],
                                    },
                                ]
                              : []),
                      ],
                  },
              }
            : {}),
        ...(!hidden("locations") && routePermissions[URL_LOCATIONS].includes(highestRole)
            ? {
                  locations: {
                      label: i18n.views.LayoutStandard.locations(),
                      key: URL_TO_KEY_MAP.URL_LOCATIONS as keyof typeof URL_TO_KEY_MAP,
                      icon: <IconContainer icon={<LocationsSvg />} />,
                      link: URL_LOCATIONS,
                      children: [
                          {
                              key: "1 locations",
                              label: i18n.containers.locations.Locations.locations(),
                              link: URL_LOCATIONS_OVERVIEW,
                              children: [],
                          },
                          {
                              key: "2 contacts",
                              label: i18n.containers.locations.Locations.contacts(),
                              link: URL_LOCATIONS_CONTACTS,
                              children: [],
                          },
                      ],
                  },
              }
            : {}),
        ...(routePermissions[URL_BOOKINGS].includes(highestRole)
            ? {
                  bookings: {
                      label: i18n.views.LayoutStandard.bookings(),
                      key: URL_TO_KEY_MAP.URL_BOOKINGS as keyof typeof URL_TO_KEY_MAP,
                      icon: <IconContainer icon={<LeftMenuBookingIcon />} />,
                      link: URL_BOOKINGS,
                      children: [
                          {
                              key: "1 bookings",
                              label: i18n.containers.bookings.Bookings.bookingsCourseList(),
                              link: URL_BOOKINGS_COURSE_LIST,
                              children: [],
                          },
                          {
                              key: "2 booking-list",
                              label: i18n.containers.bookings.Bookings.bookingList(),
                              link: URL_BOOKINGS_LIST,
                              children: [],
                          },
                      ],
                  },
              }
            : {}),
        ...(featureInterestList && !hidden("interestLists") && routePermissions[URL_INTEREST_LISTS].includes(highestRole)
            ? {
                  interestLists: {
                      label: i18n.containers.interestLists.InterestLists.menuLabel(),
                      key: URL_TO_KEY_MAP.URL_INTEREST_LISTS as keyof typeof URL_TO_KEY_MAP,
                      icon: <IconContainer icon={<SolutionOutlined />} extraClass={css.interestListsIcon} />,
                      link: URL_INTEREST_LISTS,
                      children: [
                          {
                              key: "1 interest-registrations",
                              label: i18n.containers.interestLists.InterestLists.registrationsMenu(),
                              link: URL_INTEREST_REGISTRATIONS,
                              children: [],
                          },
                          {
                              key: "2 interest-lists-manage",
                              label: i18n.containers.interestLists.InterestLists.manageMenu(),
                              link: URL_INTEREST_LISTS_MANAGE,
                              children: [],
                          },
                      ],
                  },
              }
            : {}),
        ...(featureNewsletter && routePermissions[URL_NEWSLETTER].includes(highestRole)
            ? {
                  newsletter: {
                      label: <MenuBetaLabel>{i18n.containers.newsletter.Newsletter.menuLabel()}</MenuBetaLabel>,
                      // Kein Eintrag in URL_TO_KEY_MAP: die Datei trägt Alt-Typfehler, die tsc-changed bei jeder Änderung zählt; der Schlüssel wird nur hier gebraucht.
                      key: "newsletter",
                      icon: <IconContainer icon={<MailOutlined />} extraClass={css.interestListsIcon} />,
                      link: URL_NEWSLETTER,
                      children: [
                          {
                              key: "0 newsletter-overview",
                              label: i18n.containers.newsletter.Newsletter.overviewMenu(),
                              link: URL_NEWSLETTER_OVERVIEW,
                              children: [],
                          },
                          {
                              key: "1 newsletter-lists",
                              label: i18n.containers.newsletter.Newsletter.listsMenu(),
                              link: URL_NEWSLETTER_LISTS,
                              children: [],
                          },
                          {
                              key: "2 newsletter-settings",
                              label: i18n.containers.newsletter.Newsletter.settingsMenu(),
                              link: URL_NEWSLETTER_SETTINGS,
                              children: [],
                          },
                      ],
                  },
              }
            : {}),
        ...(hasInstructorRole && routePermissions[URL_MY_BOOKINGS].includes(highestRole)
            ? {
                  myBookings: {
                      label: i18n.views.LayoutStandard.myBookings(),
                      key: URL_TO_KEY_MAP.URL_MY_BOOKINGS as keyof typeof URL_TO_KEY_MAP,
                      icon: <IconContainer icon={<LeftMenuBookingIcon />} />,
                      link: URL_MY_BOOKINGS,
                      children: [
                          {
                              key: "1 myBookings",
                              label: i18n.containers.bookings.MyBookings.bookingsCourseList(),
                              link: URL_MY_BOOKINGS_COURSE_LIST,
                              children: [],
                          },
                          {
                              key: "2 myBooking-list",
                              label: i18n.containers.bookings.MyBookings.bookingList(),
                              link: URL_MY_BOOKINGS_LIST,
                              children: [],
                          },
                      ],
                  },
              }
            : {}),
        ...(featureWeekPlaner && routePermissions[URL_LESSONS_TODAY].includes(highestRole)
            ? {
                  lessonsToday: {
                      label: i18n.views.LayoutStandard.lessonsToday(),
                      key: URL_TO_KEY_MAP.URL_LESSONS_TODAY as keyof typeof URL_TO_KEY_MAP,
                      icon: <IconContainer icon={<WeekPlannerSvg />} />,
                      link: URL_LESSONS_TODAY,
                      children: [
                          {
                              key: "1 calendar",
                              label: i18n.containers.lessonsToday.LessonsToday.weekView(),
                              link: URL_LESSONS_TODAY_CALENDAR_WEEKVIEW,
                          },
                          {
                              key: "2 calendar-dayview",
                              label: i18n.containers.lessonsToday.LessonsToday.dayView(),
                              link: URL_LESSONS_TODAY_CALENDAR_DAYVIEW,
                              children: [],
                          },
                          {
                              key: "3 calendar-old",
                              label: i18n.containers.lessonsToday.LessonsToday.lessonsToday(),
                              link: URL_LESSONS_TODAY_CALENDAR,
                              children: [],
                          },
                          ...(featureExportInstructorLessons
                              ? [
                                    {
                                        key: "4 export-instructor-lessons",
                                        label: i18n.containers.lessonsToday.LessonsToday.exportInstructorLessons(),
                                        link: URL_LESSONS_TODAY_EXPORT_INSTRUCTOR_LESSONS,
                                        children: [],
                                    },
                                ]
                              : []),
                      ],
                  },
              }
            : {}),
        ...(!hidden("invoices") && routePermissions[URL_INVOICE].includes(highestRole)
            ? {
                  invoice: {
                      label: i18n.views.LayoutStandard.invoice(),
                      key: URL_TO_KEY_MAP.URL_INVOICE as keyof typeof URL_TO_KEY_MAP,
                      icon: <IconContainer icon={<InvoicesSvg />} />,
                      link: URL_INVOICE,
                      children: [
                          {
                              key: "1 cancel-courses",
                              label: i18n.containers.invoice.Invoice.cancelCourses(),
                              link: URL_INVOICE_LIST_BY_BOOKING_UNSETTLED,
                              children: [],
                          },
                          {
                              key: "2 cancel-bookings",
                              label: i18n.containers.invoice.Invoice.cancelBookings(),
                              link: URL_INVOICE_LIST_BY_COURSE,
                              children: [],
                          },
                          {
                              key: "3 billed",
                              label: i18n.containers.invoice.Invoice.billed(),
                              link: URL_INVOICE_LIST_BY_BOOKING_SETTLED,
                              children: [],
                          },
                          {
                              key: "4 canceled",
                              label: i18n.containers.invoice.Invoice.canceled(),
                              link: URL_INVOICE_LIST_BY_BOOKING_CANCELED,
                              children: [],
                          },
                      ],
                  },
              }
            : {}),
        ...(routePermissions[URL_PAYMENTS].includes(highestRole)
            ? {
                  payments: {
                      label: i18n.views.LayoutStandard.payments(),
                      key: URL_TO_KEY_MAP.URL_PAYMENTS as keyof typeof URL_TO_KEY_MAP,
                      icon: <IconContainer icon={<TransactionsSvg />} />,
                      link: URL_PAYMENTS,
                      children: [
                          {
                              key: "1 unbilled",
                              label: i18n.containers.payments.Payments.unBilledBookings(),
                              link: URL_OPEN,
                              children: [],
                          },
                          {
                              key: "2 billed",
                              label: i18n.containers.payments.Payments.billedBookings(),
                              link: URL_PAID,
                              children: [],
                          },
                          ...(featureEsr
                              ? [
                                    {
                                        key: "3 import-history",
                                        label: "Import-Historie",
                                        link: URL_IMPORT_HISTORY,
                                        children: [],
                                    },
                                ]
                              : []),
                          // Phase 14 Plan 14-03 (D-18) — Refunds-Menu-Item
                          // (nur bei aktivem Mollie-Feature, Defense-in-Depth mit
                          // Container-seitigem Gate, siehe RefundsList.tsx).
                          ...(featureMollie
                              ? [
                                    {
                                        key: "4 refunds",
                                        label: i18n.containers.payments.Refunds.menuLabel(),
                                        link: URL_PAYMENTS_REFUNDS,
                                        children: [],
                                    },
                                ]
                              : []),
                          // Phase 16 Plan 16-05 (REPORT-01) — Mollie-Payments-Zentrale
                          // (featureMollie-gated, Container rendert null wenn off).
                          ...(featureMollie
                              ? [
                                    {
                                        key: "5 mollie-payments",
                                        label: i18n.containers.payments.MolliePayments.menuLabel(),
                                        link: URL_MOLLIE_PAYMENTS,
                                        children: [],
                                    },
                                ]
                              : []),
                          // Phase 16 Plan 16-06 (REPORT-02/REPORT-04) — Chargeback-Inbox
                          // (featureMollie-gated, absorbiert ehemalige Mollie-Retry-Queue via URL-Redirect).
                          ...(featureMollie
                              ? [
                                    {
                                        key: "6 chargebacks",
                                        label: i18n.containers.payments.Chargebacks.menuLabel(),
                                        link: URL_CHARGEBACKS,
                                        children: [],
                                    },
                                ]
                              : []),
                          // Bankabgleich (Beta): Flag je Tenant UND Rolle ab SuperAdmin.
                          ...(featureBankImport && routePermissions[URL_PAYMENTS_BANK_IMPORT].includes(highestRole)
                              ? [
                                    {
                                        key: "7 bank-import",
                                        label: i18n.containers.payments.BankImport.menuLabel(),
                                        link: URL_PAYMENTS_BANK_IMPORT,
                                        children: [],
                                    },
                                ]
                              : []),
                      ],
                  },
              }
            : {}),
        ...(routePermissions[URL_WITHDRAWALS].includes(highestRole) &&
        (withdrawalButtonEnabled || (withdrawalsOpenCount ?? 0) > 0)
            ? {
                  withdrawals: {
                      // Menü-Badge zeigt die Anzahl offener Widerrufe (status "new").
                      // Bei count 0/undefined blendet AntD den Badge aus → reines Label.
                      label: (
                          <Badge
                              count={withdrawalsOpenCount}
                              size="small"
                              offset={[10, 0]}
                          >
                              {i18n.views.LayoutStandard.withdrawals()}
                          </Badge>
                      ),
                      key: URL_TO_KEY_MAP.URL_WITHDRAWALS as keyof typeof URL_TO_KEY_MAP,
                      icon: <IconContainer icon={<WithdrawSvg />} extraClass={css.withdrawalsIcon} />,
                      link: URL_WITHDRAWALS,
                      children: [
                          {
                              key: "1 withdrawals-overview",
                              label: i18n.containers.withdrawal.Withdrawals.overview(),
                              link: URL_WITHDRAWALS_OVERVIEW,
                              children: [],
                          },
                      ],
                  },
              }
            : {}),
        ...(!hidden("staff") && routePermissions[URL_STAFF].includes(highestRole)
            ? {
                  staff: {
                      label: i18n.views.LayoutStandard.staff(),
                      key: URL_TO_KEY_MAP.URL_STAFF as keyof typeof URL_TO_KEY_MAP,
                      icon: <IconContainer icon={<StaffSvg />} extraClass={css.staffIcon} />,
                      link: URL_STAFF,
                      children: [
                          {
                              key: "1 staff-overview",
                              label: i18n.containers.staff.Staff.allStaff(),
                              link: URL_STAFF_OVERVIEW,
                              children: [],
                          },
                          ...(routePermissions[URL_STAFF_BILLING_STATUS].includes(highestRole) && featureBillingStatus
                              ? [
                                    {
                                        key: "2 billing-status",
                                        label: i18n.containers.staff.Staff.billingStatus.title(),
                                        link: URL_STAFF_BILLING_STATUS,
                                        children: [],
                                    },
                                ]
                              : []),
                          ...(routePermissions[URL_STAFF_ADMINS].includes(highestRole) && featureAdminManagement
                              ? [
                                    {
                                        key: "3 admin-management",
                                        label: i18n.containers.staff.Staff.adminManagement.title(),
                                        link: URL_STAFF_ADMINS,
                                        children: [],
                                    },
                                ]
                              : []),
                      ],
                  },
              }
            : {}),
        ...(featureAreaManager && routePermissions[URL_AREA_MANAGER].includes(highestRole)
            ? {
                  areaManager: {
                      label: <MenuBetaLabel>{i18n.views.LayoutStandard.areaManager()}</MenuBetaLabel>,
                      key: URL_TO_KEY_MAP.URL_AREA_MANAGER as keyof typeof URL_TO_KEY_MAP,
                      icon: <IconContainer icon={<ApartmentOutlined style={{fontSize: "1.2em"}} />} />,
                      link: URL_AREA_MANAGER,
                      children: [
                          {
                              key: "1 areaManager-overview",
                              label: i18n.containers.areaManager.AreaManager.overview(),
                              link: URL_AREA_MANAGER_OVERVIEW,
                              children: [],
                          },
                          {
                              key: "2 areaManager-courses",
                              label: i18n.containers.areaManager.AreaManager.courses(),
                              link: URL_AREA_MANAGER_COURSES,
                              children: [],
                          },
                          {
                              key: "3 areaManager-instructors",
                              label: i18n.containers.areaManager.AreaManager.instructors(),
                              link: URL_AREA_MANAGER_INSTRUCTORS,
                              children: [],
                          },
                          {
                              key: "4 areaManager-assistants",
                              label: i18n.containers.areaManager.AreaManager.assistants(),
                              link: URL_AREA_MANAGER_ASSISTANTS,
                              children: [],
                          },
                          {
                              key: "5 areaManager-venues",
                              label: i18n.containers.areaManager.AreaManager.venues(),
                              link: URL_AREA_MANAGER_VENUES,
                              children: [],
                          },
                      ],
                  },
              }
            : {}),
        ...(featureStaffLogin && routePermissions[URL_STAFF_LOGIN].includes(highestRole)
            ? {
                  staffLogin: {
                      label: i18n.views.LayoutStandard.staffLogin(),
                      key: URL_TO_KEY_MAP.URL_STAFF_LOGIN as keyof typeof URL_TO_KEY_MAP,
                      icon: <IconContainer icon={<StaffLoginSvg />} extraClass={css.staffLoginIcon} />,
                      link: URL_STAFF_LOGIN,
                      children: [
                          {
                              key: "1 start",
                              label: i18n.containers.staffLogin.StaffLogin.subPages.start.title(),
                              link: URL_STAFF_LOGIN_START,
                              children: [],
                          },
                          {
                              key: "2 files",
                              label: i18n.containers.staffLogin.StaffLogin.subPages.files.title(),
                              link: URL_STAFF_LOGIN_FILES,
                              children: [],
                          },
                      ],
                  },
              }
            : {}),
        ...(routePermissions[URL_TEMPLATES].includes(highestRole)
            ? {
                  templates: {
                      label: i18n.views.LayoutStandard.templates(),
                      key: URL_TO_KEY_MAP.URL_TEMPLATES as keyof typeof URL_TO_KEY_MAP,
                      icon: <IconContainer icon={<VorlagenSvg />} extraClass={css.templatesIcon} />,
                      link: URL_TEMPLATES,
                      children: [
                          {
                              key: "1 email-templates",
                              label: i18n.containers.templates.Templates.emailTemplates(),
                              link: URL_TEMPLATES_EMAIL,
                              children: [],
                          },
                          {
                              key: "2 invoice-templates",
                              label: i18n.containers.templates.Templates.invoiceTemplatesTitle(),
                              link: URL_TEMPLATES_INVOICES,
                              children: [],
                          },
                      ],
                  },
              }
            : {}),
        ...(routePermissions[URL_NOTIFICATIONS].includes(highestRole)
            ? {
                  notifications: {
                      label: i18n.views.LayoutStandard.notifications(),
                      key: URL_TO_KEY_MAP.URL_NOTIFICATIONS as keyof typeof URL_TO_KEY_MAP,
                      icon: <IconContainer icon={<NotificationsSvg />} extraClass={css.notificationsIcon} />,
                      link: URL_NOTIFICATIONS,
                      children: [
                          {
                              key: "1 sent-mails",
                              label: i18n.containers.notifications.Notifications.sentMails(),
                              link: URL_NOTIFICATIONS_SENT_MAILS,
                              children: [],
                          },
                          ...(routePermissions[URL_NOTIFICATIONS_FIXER].includes(highestRole)
                              ? [
                                    {
                                        key: "2 notification-fixer",
                                        label: i18n.containers.notifications.Notifications.notificationFixer.tableTitle(),
                                        link: URL_NOTIFICATIONS_FIXER,
                                        children: [],
                                    },
                                ]
                              : []),
                      ],
                  },
              }
            : {}),
        ...(routePermissions[URL_BADGES].includes(highestRole)
            ? {
                  badges: {
                      label: i18n.views.LayoutStandard.badges(),
                      key: URL_TO_KEY_MAP.URL_BADGES as keyof typeof URL_TO_KEY_MAP,
                      icon: <IconContainer icon={<AbzeichenSvg />} extraClass={css.badgesIcon} />,
                      link: URL_BADGES,
                      children: [
                          {
                              key: "1 badges",
                              label: i18n.containers.badges.BadgesOverview.allBadges(),
                              link: URL_BADGES,
                              children: [],
                          },
                          ...(featureExtendedBadges
                              ? [
                                    {
                                        key: "2 assign-certificates",
                                        label: i18n.containers.badges.CertificateAssign.menuLabel(),
                                        link: URL_BADGES_ASSIGN,
                                        children: [],
                                    },
                                ]
                              : []),
                      ],
                  },
              }
            : {}),
        ...(featureExtendedBadges && routePermissions[URL_CERTIFICATES].includes(highestRole)
            ? {
                  certificates: {
                      label: "Urkunden",
                      key: URL_TO_KEY_MAP.URL_CERTIFICATES as keyof typeof URL_TO_KEY_MAP,
                      icon: <IconContainer icon={<SafetyCertificateFilled />} extraClass={css.certificatesIcon} />,
                      link: URL_CERTIFICATES,
                      children: [
                          {
                              key: "1 certificate-templates",
                              label: "Vorlagen",
                              link: URL_CERTIFICATES_TEMPLATES,
                              children: [],
                          },
                          {
                              key: "2 issued-certificates",
                              label: "Ausgestellte Urkunden",
                              link: URL_CERTIFICATES_ISSUED,
                              children: [],
                          },
                      ],
                  },
              }
            : {}),
        ...(routePermissions[URL_SETTINGS].includes(highestRole)
            ? {
                  settings: {
                      label: i18n.views.LayoutStandard.settings(),
                      key: URL_TO_KEY_MAP.URL_SETTINGS as keyof typeof URL_TO_KEY_MAP,
                      icon: <IconContainer extraClass={css.settingsIcon} icon={<SettingsSvg />} />,
                      link: URL_SETTINGS,
                      children: [
                          {
                              key: "1 general",
                              label: i18n.containers.settings.SettingsAreas.menuTitle(),
                              link: URL_SETTINGS_GENERAL,
                              children: [],
                          },
                          ...(featureDataExport && routePermissions[URL_SETTINGS_DATA_EXPORT].includes(highestRole)
                              ? [
                                    {
                                        key: "3 data-export",
                                        label: i18n.containers.settings.DataExport.menuTitle(),
                                        link: URL_SETTINGS_DATA_EXPORT,
                                        children: [],
                                    },
                                ]
                              : []),
                          ...(featureDataExport && routePermissions[URL_SETTINGS_INVOICE_EXPORT].includes(highestRole)
                              ? [
                                    {
                                        key: "3b invoice-export",
                                        label: i18n.containers.settings.InvoiceExport.menuTitle(),
                                        link: URL_SETTINGS_INVOICE_EXPORT,
                                        children: [],
                                    },
                                ]
                              : []),
                          ...(routePermissions[URL_SETTINGS_ENDUSERAPP].includes(highestRole) &&
                          (highestRole === Roles.Superuser || featureWebModulAdminAccess)
                              ? [
                                    {
                                        key: "4 enduserapp",
                                        label: "Web Modul",
                                        link: URL_SETTINGS_ENDUSERAPP,
                                        children: [],
                                    },
                                ]
                              : []),
                          ...(routePermissions[URL_SETTINGS_ADMIN].includes(highestRole)
                              ? [
                                    {
                                        key: "5 admin",
                                        label: "Superuser",
                                        link: URL_SETTINGS_ADMIN,
                                        children: [],
                                    },
                                ]
                              : []),
                          ...(routePermissions[URL_SETTINGS_IMPORT_BOOKERS].includes(highestRole)
                              ? [
                                    {
                                        key: "6 import-bookers",
                                        label: "Bucher importieren",
                                        link: URL_SETTINGS_IMPORT_BOOKERS,
                                        children: [],
                                    },
                                ]
                              : []),
                          ...(routePermissions[URL_SETTINGS_MAINTENANCE].includes(highestRole)
                              ? [
                                    {
                                        key: "7 maintenance",
                                        label: i18n.containers.settings.SettingsMaintenance.menuLabel(),
                                        link: URL_SETTINGS_MAINTENANCE,
                                        children: [],
                                    },
                                ]
                              : []),
                          ...(routePermissions[URL_SETTINGS_MONITORING].includes(highestRole)
                              ? [
                                    {
                                        key: "8 monitoring-settings",
                                        label: "Monitoring",
                                        link: URL_SETTINGS_MONITORING,
                                        children: [],
                                    },
                                ]
                              : []),
                      ],
                  },
              }
            : {}),
        // Hilfe-Center: Fernwartung, Doku, Kontakt. Für alle Rollen, kein Feature-Gate.
        help: {
            label: i18n.views.LayoutStandard.help(),
            key: "help",
            icon: <IconContainer icon={<CustomerServiceOutlined />} />,
            link: URL_HELP,
            children: [],
        },
    };

    return mainMenuItems;
};
